import React, { useEffect, useState } from 'react';
import './StudyAbroadPaymentWall.scss';
import { usePurchaseHistory } from '@/Recoil/Payment/Purchase';
import { navigateTo } from '@/Utils/routing';
import { createI18n } from '@/Utils/i18n';

const translations = {
    'Unlock Premium Study Abroad Support': { ko: '프리미엄 유학 지원 서비스', id: 'Dukungan Studi Luar Negeri Premium' },
    'Get personalized guidance for your study abroad journey': { ko: '맞춤형 유학 가이드를 받아보세요', id: 'Dapatkan panduan pribadi untuk perjalanan studi Anda' },
    'View Details': { ko: '자세히 보기', id: 'Lihat Detail' },
    'Maybe Later': { ko: '나중에', id: 'Nanti Saja' }
};

const t = createI18n(translations);

interface PaymentWallProps {
    productid: string;
    forceShow?: boolean;
}

const StudyAbroadPaymentWall: React.FC<PaymentWallProps> = ({
    productid,
    forceShow = false
}) => {
    const { hasProduct, loading, error } = usePurchaseHistory();
    const [hasScrolledEnough, setHasScrolledEnough] = useState(false);

    // Remove sessionStorage check - payment wall should show on every page load for unpaid users
    // Users can dismiss it per page, but it will show again on next page/reload

    useEffect(() => {
        let scrollTimeout: NodeJS.Timeout;

        const handleScroll = () => {
            clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                const scrollY = window.scrollY;
                const viewportHeight = window.innerHeight * 0.5;

                if (scrollY > viewportHeight) {
                    setHasScrolledEnough(true);
                } else {
                    setHasScrolledEnough(false);
                }
            }, 100);
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => {
            window.removeEventListener('scroll', handleScroll);
            clearTimeout(scrollTimeout);
        };
    }, []);

    // No dismiss functionality - payment wall cannot be dismissed

    const handleViewDetails = () => {
        navigateTo(`/product/study-abroad-mate`);
    };

    const isVisible = !loading && (!hasProduct(productid) || error) && (forceShow || hasScrolledEnough);
    // Always show overlay for unpaid users, but popup can be dismissed
    if (!isVisible) return null;

    return (<>
        {/* Page overlay with white gradient - always show for unpaid users */}
        <div className="payment-wall-overlay"></div>
        <div className="payment-wall-container">
            <div className="payment-wall">
                {/* No close button - cannot be dismissed */}

                <div className="payment-wall-content">
                    <div className="icon-container">
                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="24" cy="24" r="20" fill="url(#gradient1)" fillOpacity="0.1"/>
                            <path d="M24 14V34M24 14L18 20M24 14L30 20" stroke="url(#gradient2)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M34 28C36.2091 28 38 26.2091 38 24C38 21.7909 36.2091 20 34 20C33.6357 20 33.2834 20.0511 32.95 20.1462C32.9785 19.9327 33 19.7173 33 19.5C33 16.4624 30.5376 14 27.5 14C24.8803 14 22.6896 15.8647 22.1406 18.3563C21.6253 18.1291 21.0569 18 20.5 18C18.567 18 17 19.567 17 21.5C17 21.8442 17.0441 22.1785 17.1267 22.4975C15.3239 22.8412 14 24.3705 14 26.25C14 28.3211 15.6789 30 17.75 30" stroke="url(#gradient2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <defs>
                                <linearGradient id="gradient1" x1="4" y1="4" x2="44" y2="44">
                                    <stop offset="0%" stopColor="#7b4397"/>
                                    <stop offset="100%" stopColor="#dc2430"/>
                                </linearGradient>
                                <linearGradient id="gradient2" x1="14" y1="14" x2="34" y2="34">
                                    <stop offset="0%" stopColor="#7b4397"/>
                                    <stop offset="100%" stopColor="#dc2430"/>
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>

                    <div className="text-content">
                        <h3>{t('Unlock Premium Study Abroad Support')}</h3>
                        <p>{t('Get personalized guidance for your study abroad journey')}</p>
                    </div>

                    <div className="action-buttons">
                        <button className="primary-button" onClick={handleViewDetails}>
                            {t('View Details')}
                        </button>
                        {/* No dismiss button - cannot be dismissed */}
                    </div>
                </div>
            </div>
        </div>
    </>);
};

export default StudyAbroadPaymentWall;